import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import CurrencyContext from 'contexts/currencyContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanViewLuxPlusBenefits, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { getOfferValueDisplayVisibility } from 'lib/offer/getOfferDisplayPricing'
import PriceDetails from 'tripPlanner/components/ItemOfferPrice/PriceDetails'
import type { DrawerHotelPrice, DrawerHotelUnavailableReason } from './types'
import useWarnMissingCurrency from 'tripPlanner/pages/chat/ChatTripPlannerPage/hooks/useWarnMissingCurrency'
import { getAccommodationUnavailableLabel } from 'tripPlanner/utils/accommodationPricingDisplay'
import { useOrder } from 'tripPlanner/hooks/api/order'
import { getBookedOfferPrice } from 'tripPlanner/utils/bookedOfferPricing'
import BookedPriceDetails from 'tripPlanner/components/ItemOfferPrice/BookedPriceDetails'
import LuxPlusOfferVoucherCallout from 'luxPlus/components/LuxPlusOfferVoucherCallout'

interface Props {
  price?: DrawerHotelPrice
  luxPlusPrice?: number
  value?: number
  memberValue?: number
  discountPercent?: number
  memberDiscountPercent?: number
  isTotal?: boolean
  includesTaxesAndFees?: boolean
  unavailableReason?: DrawerHotelUnavailableReason
  offerId: string
  saleUnit?: string
  isBooked?: boolean
  orderId?: string
  offer?: App.AnyOffer
}

function HotelDetailPricing({
  price,
  luxPlusPrice,
  value,
  memberValue,
  discountPercent,
  memberDiscountPercent,
  isTotal,
  includesTaxesAndFees,
  unavailableReason,
  offerId,
  saleUnit,
  isBooked,
  orderId,
  offer,
}: Props) {
  const intl = useIntl()
  const currentCurrency = useContext(CurrencyContext)
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const showLuxPlusPricing = luxPlusEnabled && !!luxPlusPrice && !!price && luxPlusPrice < price.total

  const missingCurrencyContext = useMemo(() => ({ offerId }), [offerId])
  useWarnMissingCurrency(
    Boolean(price && !price.currency),
    '[TripChat] Hotel detail pricing missing currency',
    missingCurrencyContext,
  )

  const bookingDetailsLabel = intl.formatMessage({
    id: 'V3if7yCh9Y75j9TcYdbF',
    defaultMessage: 'Booking details',
    description: 'CTA button text to view booking details in trip planner',
  })

  // The order carries what was actually charged; the offer `price` prop is the current
  // sell price, which drifts from the paid amount once a booking exists.
  const { data: bookedOrder, isLoading: isBookedOrderLoading } = useOrder(
    isBooked ? orderId : undefined,
  )
  const bookedPrice = useMemo(
    () => (isBooked ? getBookedOfferPrice(bookedOrder, offerId) : undefined),
    [isBooked, bookedOrder, offerId],
  )

  if (isBooked) {
    const bookingDetailsLink = (
      <TextLink
        size="medium"
        href={orderId ? `/account/my-escapes/order/${orderId}` : '/account/my-escapes'}
        target="_blank"
        rel="noopener noreferrer"
      >
        {bookingDetailsLabel}
      </TextLink>
    )

    // Nothing to balance the link against, so keep it right-aligned on its own.
    if (!isBookedOrderLoading && !bookedPrice) {
      return (
        <Group direction="horizontal" horizontalAlign="end">
          {bookingDetailsLink}
        </Group>
      )
    }

    return (
      <Group
        direction="horizontal"
        gap={16}
        horizontalAlign="space-between"
        verticalAlign="end"
        wrap="wrap"
        fullWidth
      >
        <BookedPriceDetails price={bookedPrice} isLoading={isBookedOrderLoading} />
        {bookingDetailsLink}
      </Group>
    )
  }

  if (!price && unavailableReason) {
    return (
      <BodyText variant="large" weight="bold">
        {getAccommodationUnavailableLabel(unavailableReason, intl)}
      </BodyText>
    )
  }

  if (!price) return null

  const priceLabel = isTotal
    ? intl.formatMessage(
        {
          id: 'qJsLbdcZF_W4ZSzCFkgd',
          defaultMessage: '{count, plural, one {# night} other {# nights}} total',
          description: 'Hotel pricing caption showing a selected-rate total in the trip planner AI chat',
        },
        { count: price.nights },
      )
    : intl.formatMessage(
        {
          id: 'TmpthJ75SbMB0p3uodV5',
          defaultMessage: '{count, plural, one {# night} other {# nights}} from',
          description: 'Hotel pricing caption showing number of nights in the trip planner AI chat',
        },
        { count: price.nights },
      )
  const isMemberDiscountShown = showLuxPlusPricing && canViewLuxPlusBenefits && memberDiscountPercent != null
  const effectiveDiscountPercent = isMemberDiscountShown ? memberDiscountPercent : discountPercent
  // TRP-4392: value must come from the SAME basis as the discount being shown, or the
  // strikethrough RRP, the price and the percentage stop reconciling for a member viewer.
  // memberValue falls back to the standard value when the source has no distinct member
  // value leg — mirrors the backend's own fallback (priceOption.ts).
  const effectiveValue = isMemberDiscountShown ? (memberValue ?? value) : value
  const { shouldShowValue, shouldShowDiscount } = getOfferValueDisplayVisibility({
    value: effectiveValue,
    discountPercent: effectiveDiscountPercent,
    shouldDisplayValue: true,
  })

  return (
    <VerticalSpacer gap={8}>
      {offer && <LuxPlusOfferVoucherCallout offer={offer} mode="offer" align="start" />}
      <PriceDetails
        price={price.total}
        luxPlusPrice={luxPlusPrice}
        currencyCode={price.currency ?? currentCurrency}
        priceLabel={priceLabel}
        totalLabel={isTotal ? 'total' : saleUnit ?? 'total'}
        value={shouldShowValue ? effectiveValue : undefined}
        discount={shouldShowDiscount ? effectiveDiscountPercent : undefined}
        discountDisplayType="following"
        showTaxesAndFees={includesTaxesAndFees ?? false}
        showValueWithoutDiscount
      />
    </VerticalSpacer>
  )
}

export default HotelDetailPricing
